import { ButtonKinds, Icon, Button, Modal, Text, DesignLanguage, useIsDesignLanguage, Theme, useIsTheme } from '@afterpaytouch/core'
import React, { FC, ReactNode, useMemo } from 'react'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './styles.module.css'
import { Trans, useTranslation } from '../../utils/convergence'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent, AutoPayModalKey, AutopayConfirmationModalKey } from '../../model'
import { PaymentAuthorizationLink } from '../PaymentAuthorizationLink'
import { createDocuments, useAppDispatch, useIsAfterpayFinanceFeeEligible, useIsApplePaylater, useIsConsumerLendingOrder } from '../../state'
import { TruthInLendingButton } from '../TruthInLending/TruthInLendingButton'
import { TruthInLendingButton as ApplePaylateTruthInLendingButton } from '../../flow/apple-paylater/components/TruthInLendingButton'

interface AutopayModalProps {
  show: boolean
  autoPayAgreed: boolean
  onCloseModal: () => void
  onAutopayStatusChange?: (value: boolean) => void
  modalKey?: string
}

type ModalVariant = 'default' | 'aff' | 'paymentMonthly'
type ModalMode = 'on' | 'off'
interface ModalTranslationCopy {
  heading: string
  description: string
  close: string
  cta: string
}

type ModalTranslationFactory = () => ModalTranslationCopy
type ModalTranslations = Record<ModalVariant, Record<ModalMode, ModalTranslationFactory>>

export const AutopayModal: FC<AutopayModalProps> = ({ show, autoPayAgreed, onCloseModal, onAutopayStatusChange, modalKey = AutoPayModalKey }) => {
  const dispatch = useAppDispatch()
  const { headingSize, smallTextSize } = useCheckoutSizes()
  const isPaymentMonthly = useIsConsumerLendingOrder()
  const isApplePaylater = useIsApplePaylater()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const { t } = useTranslation()
  const isSummaryModal = modalKey === AutoPayModalKey

  const getVariant = (): ModalVariant => {
    if (isPaymentMonthly) {
      return 'paymentMonthly'
    }

    if (isAFF) {
      return 'aff'
    }

    return 'default'
  }

  const getMode = (): ModalMode => {
    if (autoPayAgreed) {
      return 'off'
    }

    return 'on'
  }

  const variant = getVariant()
  const mode = getMode()
  const isAutopayOnMode = mode === 'on'

  const getTranslations = (): ModalTranslations => {
    return {
      paymentMonthly: {
        on: () => ({
          heading: t('summary:autopay.modal_paymentMonthly.heading_on'),
          description: t('summary:autopay.modal_paymentMonthly.description_on'),
          close: t('summary:autopay.modal_paymentMonthly.close'),
          cta: t('summary:autopay.modal_paymentMonthly.cta_on'),
        }),
        off: () => ({
          heading: t('summary:autopay.modal_paymentMonthly.heading_off'),
          description: t('summary:autopay.modal_paymentMonthly.description_off'),
          close: t('summary:autopay.modal_paymentMonthly.close'),
          cta: t('summary:autopay.modal_paymentMonthly.cta_off'),
        }),
      },
      aff: {
        on: () => ({
          heading: t('summary:autopay.modal_aff.heading_on'),
          description: t('summary:autopay.modal_aff.description_on'),
          close: t('summary:autopay.modal_aff.close'),
          cta: t('summary:autopay.modal_aff.cta_on'),
        }),
        off: () => ({
          heading: t('summary:autopay.modal_aff.heading_off'),
          description: t('summary:autopay.modal_aff.description_off'),
          close: t('summary:autopay.modal_aff.close'),
          cta: t('summary:autopay.modal_aff.cta_off'),
        }),
      },
      default: {
        on: () => ({
          heading: t('summary:autopay.modal.heading_on'),
          description: t('summary:autopay.modal.description_on'),
          close: t('summary:autopay.modal.close'),
          cta: t('summary:autopay.modal.cta_on'),
        }),
        off: () => ({
          heading: t('summary:autopay.modal.heading_off'),
          description: t('summary:autopay.modal.description_off'),
          close: t('summary:autopay.modal.close'),
          cta: t('summary:autopay.modal.cta_off'),
        }),
      },
    }
  }

  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isCashTheme = useIsTheme(Theme.Cash)
  const translations = getTranslations()
  const selectedTranslation = translations[variant][mode]()
  const defaultTranslation = variant === 'default' ? selectedTranslation : translations.default[mode]()
  const heading = selectedTranslation.heading
  const description = selectedTranslation.description
  const closeText = selectedTranslation.close
  const ctaText = isSummaryModal ? defaultTranslation.cta : selectedTranslation.cta
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const ctaButtonKind: ButtonKinds = autoPayAgreed ? 'Error' : 'Primary'

  const eventName = useMemo(() => {
    if (isPaymentMonthly) {
      return autoPayAgreed ? TrackingEvent.VIEWED_AUTOPAY_OFF_PAGE : TrackingEvent.VIEWED_AUTOPAY_ON_PAGE
    }
    return TrackingEvent.VIEWED_AUTOPAY_HALF_SHEET
  }, [autoPayAgreed, isPaymentMonthly])

  const onCloseButtonClick = (): void => {
    onCloseModal()

    if (isPaymentMonthly) {
      logEvent(autoPayAgreed ? TrackingEvent.PRESSED_AUTOPAY_OFF_PAGE_CLOSE_BUTTON : TrackingEvent.PRESSED_AUTOPAY_ON_PAGE_CLOSE_BUTTON)
    } else {
      logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_AUTOPAY_CLOSE_BUTTON)
    }
  }

  const onCtaButtonClick = (): void => {
    const autopay = !autoPayAgreed

    if (isPaymentMonthly || isAFF) {
      dispatch(createDocuments({ autopay }))
    }
    onAutopayStatusChange?.(autopay)
    onCloseModal()
  }

  const components = {
    paymentAuthorization: <PaymentAuthorizationLink />,
  }

  const renderCtaButton = (): ReactNode => {
    if (modalKey === AutopayConfirmationModalKey) {
      if (isApplePaylater) {
        return <ApplePaylateTruthInLendingButton />
      }
      return <TruthInLendingButton />
    }

    return (
      <Button kind={ctaButtonKind} padding='Fluid' testNameSpace='autopay-cta' onClick={onCtaButtonClick}>
        {ctaText}
      </Button>
    )
  }

  return (
    <Modal id={modalKey} showCloseButton={isBronteDesignLanguage} onBackdropClick={onCloseModal} testNameSpace='Autopay' show={show}>
      <Modal.Header>
        <div className={styles.autoPayModalIcon}>
          {autoPayAgreed ? (
            isCashTheme ? (
              <Icon name='AlertFill' color='Error' size='Large' />
            ) : (
              <Icon name='RecurringAutomatic' color='Prominent' size='Large' />
            )
          ) : (
            <Icon name='RecurringAutomatic' color={isCashTheme ? 'Brand' : 'Prominent'} size='Large' />
          )}
        </div>
        <Text bold size={headingSize}>
          {heading}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Text color='Subtle'>{description}</Text>
        {isAutopayOnMode && !isPaymentMonthly && !isAFF && (
          <p className={styles.autoPayAgreementSection}>
            <Text renderAs='span' color='Subtle' size={smallTextSize}>
              <Trans i18nKey='summary:autopay.modal.agreement' components={components} />
            </Text>
          </p>
        )}
        <PaymentAuthorizationLink />
      </Modal.Content>
      <Modal.Footer>
        {renderCtaButton()}
        <Button onClick={onCloseButtonClick} kind='Secondary' padding='Fluid' testNameSpace='autopay-close'>
          {closeText}
        </Button>
      </Modal.Footer>
      <TrackOnMount eventName={eventName} />
    </Modal>
  )
}
